import { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon, ChevronDown, ChevronRight, History, Loader2, MinusCircle, PlusCircle, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '~/lib/utils';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type BundleVoucherRow = {
    id: string;
    customerId: number;
    customerFirstName: string;
    customerLastName: string;
    customerEmail: string | null;
    treatmentVariantId: string;
    treatmentVariantName: string;
    unitPriceGross: number;
    unitQuantity: number;
    usedUnits: number;
    remainingUnits: number;
    totalValueEur: number;
    remainingValueEur: number;
    sourceBillingDraftId: string;
    sourceBillingDraftNumber: string;
    sourceBillingDraftDate: Date;
    invoiceId: string;
    invoiceNumber: string;
    createdAt: Date;
    transactionCount: number;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

// Expanded row component for transaction history
const ExpandedRowContent = ({
    voucher,
    isAdmin,
}: {
    voucher: BundleVoucherRow;
    isAdmin: boolean;
}) => {
    const navigate = useNavigate();
    const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
    const [adjustmentUnits, setAdjustmentUnits] = useState<number>(0);
    const [adjustmentReason, setAdjustmentReason] = useState('');
    const trpcUtils = trpc.useUtils();

    const { data: transactions, isLoading } = trpc.customerMoney.getBundleVoucherTransactions.useQuery(
        { bundleVoucherId: voucher.id }
    );

    const adjustMutation = trpc.customerMoney.adjustBundleVoucher.useMutation({
        onSuccess: () => {
            toast.success('Gutschein wurde angepasst');
            trpcUtils.customerMoney.getAllBundleVouchers.invalidate();
            trpcUtils.customerMoney.getBundleVoucherTransactions.invalidate({ bundleVoucherId: voucher.id });
            setShowAdjustmentForm(false);
            setAdjustmentUnits(0);
            setAdjustmentReason('');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleAdjust = () => {
        if (adjustmentUnits === 0 || !adjustmentReason.trim()) {
            return;
        }
        adjustMutation.mutate({
            bundleVoucherId: voucher.id,
            quantityUnits: adjustmentUnits,
            reason: adjustmentReason.trim(),
        });
    };

    return (
        <div className='p-4 bg-muted/30 space-y-4'>
            <div className='flex items-center gap-2'>
                <History className='h-4 w-4' />
                <span className='text-sm font-medium'>
                    Transaktionen ({voucher.transactionCount})
                </span>
            </div>

            {isLoading ? (
                <div className='space-y-1'>
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className='h-8 w-full' />
                    ))}
                </div>
            ) : transactions && transactions.length > 0 ? (
                <div className='space-y-1 max-h-40 overflow-y-auto'>
                    {transactions.map((tx) => (
                        <div
                            key={tx.id}
                            className='flex justify-between items-center text-xs py-2 px-3 bg-background rounded border'
                        >
                            <div className='flex items-center gap-2 flex-wrap'>
                                <span className={tx.quantityUnits > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                    {tx.quantityUnits > 0 ? '+' : ''}{tx.quantityUnits} Einheiten
                                </span>
                                <span className='text-muted-foreground'>
                                    {tx.type === 'REDEMPTION' && 'Eingelöst'}
                                    {tx.type === 'REFUND' && 'Erstattet'}
                                    {tx.type === 'ADJUSTMENT' && 'Korrektur'}
                                </span>
                                {tx.billingDraftLineItem?.invoice && (() => {
                                    const stornoInvoice = tx.type === 'REFUND'
                                        ? tx.billingDraftLineItem.invoice.refundInvoices?.[0]
                                        : null;
                                    const displayInvoice = stornoInvoice ?? tx.billingDraftLineItem.invoice;
                                    return (
                                        <span
                                            className='font-mono text-primary cursor-pointer hover:underline'
                                            onClick={() => navigate(`/invoice/${displayInvoice.id}`)}
                                        >
                                            {stornoInvoice ? 'Storno-Rg.' : 'Rg.'} {displayInvoice.invoiceNumber}
                                        </span>
                                    );
                                })()}
                                {tx.description && (
                                    <span className='text-muted-foreground'>- {tx.description}</span>
                                )}
                            </div>
                            <span className='text-muted-foreground shrink-0 ml-2'>
                                {format(new Date(tx.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className='text-sm text-muted-foreground'>
                    Noch keine Transaktionen
                </p>
            )}

            {/* Admin adjustment section */}
            {/*
            {isAdmin && (
                <>
                    <Separator />
                    {!showAdjustmentForm ? (
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => setShowAdjustmentForm(true)}
                        >
                            Manuell anpassen
                        </Button>
                    ) : (
                        <div className='space-y-3 p-3 border rounded-lg bg-background'>
                            <div className='space-y-2'>
                                <Label>Einheiten hinzufügen/entfernen</Label>
                                <div className='flex items-center gap-2'>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => setAdjustmentUnits(adjustmentUnits - 1)}
                                    >
                                        <MinusCircle className='h-4 w-4' />
                                    </Button>
                                    <Input
                                        type='number'
                                        value={adjustmentUnits}
                                        onChange={(e) => setAdjustmentUnits(parseInt(e.target.value) || 0)}
                                        className='w-20 text-center'
                                    />
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => setAdjustmentUnits(adjustmentUnits + 1)}
                                    >
                                        <PlusCircle className='h-4 w-4' />
                                    </Button>
                                </div>
                            </div>
                            <div className='space-y-2'>
                                <Label>Begründung *</Label>
                                <Textarea
                                    value={adjustmentReason}
                                    onChange={(e) => setAdjustmentReason(e.target.value)}
                                    placeholder='Grund für die Anpassung...'
                                    rows={2}
                                />
                            </div>
                            <div className='flex gap-2'>
                                <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() => {
                                        setShowAdjustmentForm(false);
                                        setAdjustmentUnits(0);
                                        setAdjustmentReason('');
                                    }}
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    size='sm'
                                    onClick={handleAdjust}
                                    disabled={adjustmentUnits === 0 || !adjustmentReason.trim() || adjustMutation.isPending}
                                >
                                    {adjustMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                    Anpassen
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
            */}
        </div>
    );
};

const Bundles = () => {
    const navigate = useNavigate();
    const { user } = useUserStore();
    const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.OPERATOR;

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'exhausted'>('all');
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [isSelectingToDate, setIsSelectingToDate] = useState(false);
    const [treatmentFilter, setTreatmentFilter] = useState('');
    const [page, setPage] = useState(0);
    const pageSize = 20;

    // Fetch bundle vouchers
    const { data, isLoading } = trpc.customerMoney.getAllBundleVouchers.useQuery({
        page,
        pageSize,
        statusFilter,
        searchQuery: searchQuery || undefined,
        dateFrom: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
        dateTo: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
        treatmentVariantName: treatmentFilter || undefined,
    });

    const columns: ColumnDef<BundleVoucherRow>[] = [
        {
            id: 'expander',
            header: () => null,
            cell: ({ row }) => (
                <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                >
                    {row.getIsExpanded() ? (
                        <ChevronDown className='h-4 w-4' />
                    ) : (
                        <ChevronRight className='h-4 w-4' />
                    )}
                </Button>
            ),
        },
        {
            accessorKey: 'createdAt',
            header: 'Datum',
            cell: ({ row }) => (
                <span className='text-xs'>
                    {format(new Date(row.original.createdAt), 'dd.MM.yyyy', { locale: de })}
                </span>
            ),
        },
        {
            accessorKey: 'invoiceNumber',
            header: 'Rechnung',
            cell: ({ row }) => (
                <div
                    className='font-mono text-xs cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/invoice/${row.original.invoiceId}`);
                    }}
                >
                    {row.original.invoiceNumber}
                </div>
            ),
        },
        {
            accessorKey: 'sourceBillingDraftNumber',
            header: 'Entwurf',
            cell: ({ row }) => (
                <div
                    className='font-mono text-xs cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/billing-draft/${row.original.sourceBillingDraftId}`);
                    }}
                >
                    {row.original.sourceBillingDraftNumber}
                </div>
            ),
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => (
                <div>
                    <div
                        className='cursor-pointer hover:underline font-medium text-sm'
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customer/${row.original.customerId}`);
                        }}
                    >
                        {row.original.customerFirstName} {row.original.customerLastName}
                    </div>
                    {row.original.customerEmail && (
                        <div className='text-xs text-muted-foreground'>{row.original.customerEmail}</div>
                    )}
                </div>
            ),
        },
        {
            accessorKey: 'treatmentVariantName',
            header: 'Behandlung',
            cell: ({ row }) => (
                <span className='font-medium text-sm'>{row.original.treatmentVariantName}</span>
            ),
        },
        {
            id: 'value',
            header: 'Wert',
            cell: ({ row }) => (
                <span className='font-mono text-sm'>
                    {formatCurrency(row.original.totalValueEur)}
                </span>
            ),
        },
        {
            id: 'sessions',
            header: 'Sitzungen',
            cell: ({ row }) => {
                const { usedUnits, unitQuantity } = row.original;
                return (
                    <span className='font-mono text-sm'>
                        {usedUnits} / {unitQuantity}
                    </span>
                );
            },
        },
        {
            accessorKey: 'remainingValueEur',
            header: 'Restwert',
            cell: ({ row }) => (
                <span className={row.original.remainingUnits > 0 ? 'text-green-600 font-medium text-sm' : 'text-muted-foreground text-sm'}>
                    {formatCurrency(row.original.remainingValueEur)}
                </span>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const { usedUnits, remainingUnits, unitQuantity } = row.original;
                if (usedUnits === 0 && remainingUnits === unitQuantity) {
                    return (
                        <Badge variant='outline' className='bg-blue-50 text-blue-700 border-blue-200'>
                            Neu
                        </Badge>
                    );
                }
                if (remainingUnits > 0) {
                    return (
                        <Badge variant='default' className='bg-green-100 text-green-800 hover:bg-green-100'>
                            Aktiv
                        </Badge>
                    );
                }
                return (
                    <Badge variant='outline'>
                        Aufgebraucht
                    </Badge>
                );
            },
        },
    ];

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        manualPagination: true,
        pageCount: data?.pageCount ?? 0,
    });

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex justify-between items-center mb-6'>
                    <h1 className='text-2xl font-bold'>Bundle-Gutscheine</h1>
                </div>

                {/* Filters */}
                <Card className='mb-6'>
                    <CardContent className='pt-6'>
                        <div className='flex flex-wrap gap-4'>
                            <div className='flex-1 min-w-[200px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Suche</Label>
                                <div className='relative'>
                                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Kunde, Rechnungs- oder Entwurfsnummer...'
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setPage(0);
                                        }}
                                        className='pl-10'
                                    />
                                </div>
                            </div>
                            <div className='min-w-[150px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Behandlung</Label>
                                <Input
                                    placeholder='Behandlung...'
                                    value={treatmentFilter}
                                    onChange={(e) => {
                                        setTreatmentFilter(e.target.value);
                                        setPage(0);
                                    }}
                                />
                            </div>
                            <div className='min-w-[200px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Zeitraum</Label>
                                <Popover
                                    open={dateRangeOpen}
                                    onOpenChange={(open) => {
                                        setDateRangeOpen(open);
                                        if (open) {
                                            setIsSelectingToDate(false);
                                        }
                                    }}
                                >
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant='outline'
                                            className={cn(
                                                'w-full justify-start text-left font-normal',
                                                !dateFrom && !dateTo && 'text-muted-foreground'
                                            )}
                                        >
                                            <CalendarIcon className='mr-2 h-4 w-4' />
                                            {dateFrom && dateTo ? (
                                                `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ${format(dateTo, 'dd.MM.yyyy', { locale: de })}`
                                            ) : dateFrom ? (
                                                `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ...`
                                            ) : (
                                                'Zeitraum wählen'
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-auto p-0' align='start'>
                                        <div className='p-3 border-b h-[52px] flex flex-col justify-center'>
                                            <p className='text-sm text-muted-foreground'>
                                                {isSelectingToDate ? 'Wähle das Enddatum' : 'Wähle das Startdatum'}
                                            </p>
                                            <p className='text-xs text-muted-foreground h-4'>
                                                {dateFrom && isSelectingToDate
                                                    ? `Start: ${format(dateFrom, 'dd. MMM yyyy', { locale: de })}`
                                                    : '\u00A0'
                                                }
                                            </p>
                                        </div>
                                        <Calendar
                                            mode='range'
                                            locale={de}
                                            selected={
                                                isSelectingToDate
                                                    ? (dateFrom ? { from: dateFrom, to: dateFrom } : undefined)
                                                    : undefined
                                            }
                                            onDayClick={(day) => {
                                                if (!isSelectingToDate) {
                                                    setDateFrom(day);
                                                    setDateTo(undefined);
                                                    setIsSelectingToDate(true);
                                                } else {
                                                    let finalFrom = dateFrom;
                                                    let finalTo = day;

                                                    if (dateFrom && day < dateFrom) {
                                                        finalFrom = day;
                                                        finalTo = dateFrom;
                                                    }

                                                    setDateFrom(finalFrom);
                                                    setDateTo(finalTo);
                                                    setIsSelectingToDate(false);
                                                    setDateRangeOpen(false);
                                                    setPage(0);
                                                }
                                            }}
                                            numberOfMonths={2}
                                            defaultMonth={dateFrom || new Date()}
                                        />
                                        {(dateFrom || dateTo) && (
                                            <div className='p-2 border-t flex justify-end'>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    onClick={() => {
                                                        setDateFrom(undefined);
                                                        setDateTo(undefined);
                                                        setIsSelectingToDate(false);
                                                        setDateRangeOpen(false);
                                                        setPage(0);
                                                    }}
                                                >
                                                    Zurücksetzen
                                                </Button>
                                            </div>
                                        )}
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className='min-w-[150px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Status</Label>
                                <Select
                                    value={statusFilter}
                                    onValueChange={(value: 'all' | 'active' | 'exhausted') => {
                                        setStatusFilter(value);
                                        setPage(0);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Alle Status' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='all'>Alle Status</SelectItem>
                                        <SelectItem value='active'>Aktiv</SelectItem>
                                        <SelectItem value='exhausted'>Aufgebraucht</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card>
                    <div className='rounded-md border'>
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            {columns.map((_, j) => (
                                                <TableCell key={j}>
                                                    <Skeleton className='h-4 w-full' />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : table.getRowModel().rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className='text-center py-8 text-muted-foreground'>
                                            Keine Bundle-Gutscheine gefunden
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    table.getRowModel().rows.map((row) => (
                                        <>
                                            <TableRow
                                                key={row.id}
                                                className='cursor-pointer hover:bg-muted/50'
                                                onClick={() => row.toggleExpanded()}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell key={cell.id}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                            {row.getIsExpanded() && (
                                                <TableRow key={`${row.id}-expanded`}>
                                                    <TableCell colSpan={columns.length} className='p-0'>
                                                        <ExpandedRowContent
                                                            voucher={row.original}
                                                            isAdmin={isAdmin}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </Card>

                {/* Pagination */}
                {data && data.pageCount > 1 && (
                    <div className='flex items-center justify-between mt-4'>
                        <div className='text-sm text-muted-foreground'>
                            Seite {page + 1} von {data.pageCount} ({data.totalCount} Einträge)
                        </div>
                        <div className='flex gap-2'>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setPage(Math.max(0, page - 1))}
                                disabled={page === 0}
                            >
                                Zurück
                            </Button>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setPage(Math.min(data.pageCount - 1, page + 1))}
                                disabled={page >= data.pageCount - 1}
                            >
                                Weiter
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Content>
    );
};

export default Bundles;
